import React from 'react';
import styled from 'styled-components';

interface IProps extends React.InputHTMLAttributes<HTMLButtonElement> {
    id?: string;
    children: JSX.Element | string;
    onClick: (...props: any) => void;
    variant?: TButtonVariants;
    disabled?: boolean;
    pillStyle?: boolean;
    concise?: boolean;
    slim?: boolean;
}

const BlockButton = ({
    children,
    concise = false,
    disabled,
    id,
    onClick,
    pillStyle = false,
    slim = false,
    variant,
}: IProps): JSX.Element => (
    <ButtonContainer
        data-qa={`btn_disabled_${id || children}`}
        disabled={disabled}
        onClick={onClick}
        variant={variant}
        pillStyle={pillStyle}
        concise={concise}
        slim={slim}>
        <ButtonContent data-qa={`btn_${id || children}`}>{children}</ButtonContent>
    </ButtonContainer>
);

type TButtonVariants = 'primary' | 'secondary' | 'support' | 'tertiary' | 'warning' | null;

type TButtonProps = {
    variant?: TButtonVariants;
    pillStyle?: boolean;
    concise?: boolean;
    slim?: boolean;
};

const ButtonContainer = styled.button<TButtonProps>`
    width: ${({ concise }): string => `${concise ? 'fit-content' : '100%'}`};

    height: ${({ slim }): string => `${slim ? '40px' : '48px'}`};
    padding: ${({ pillStyle }): string => `${pillStyle ? '0 32px' : '0 24px'}`};
    white-space: nowrap;
    background-color: ${({ theme }): string => theme.colors.brandPrimary};
    color: ${({ theme }): string => theme.colors.bodyAlt};
    border-radius: ${({ pillStyle, theme }): string => `${pillStyle ? '24px' : theme.metrics.borderRadius}`};
    position: relative;
    overflow: hidden;
    transition: top ease-in-out 0.2s, right ease-in-out 0.2s, left ease-in-out 0.2s;

    &:after {
        content: '';
        position: absolute;
        bottom: 0;
        ${({ theme: { left } }): string => `${left}: 0`};
        width: 0%;
        height: 100%;
        background-color: rgba(0, 0, 0, 0.15);
        transition: width 0.15s ease-in-out 0.2s;
        z-index: 1;
    }

    &:active {
        top: 1px;
        ${({ theme: { right } }): string => `${right}: -1px`};
    }

    ${({ theme, variant }): string => {
        switch (variant) {
            case 'primary':
                return `
                `;
            case 'secondary':
                return `
                    background-color: ${theme.colors.bgPrimary};
                    border: 1px solid;
                    color: ${theme.colors.brandPrimary};

                    &:after {
                        background-color: rgba(0, 0, 0, 0.05);
                    }
                    `;
            case 'support':
                return `
                    background-color: ${theme.colors.bgPrimary};
                    border: 1px solid;
                    color: ${theme.colors.bodyLight};

                    &:after {
                        background-color: rgba(0, 0, 0, 0.05);
                    }
                    `;
            case 'tertiary':
                return `
                    background-color: ${theme.colors.bgPrimary};
                    border: 1px solid;
                    color: ${theme.colors.body};

                    &:after {
                        background-color: rgba(0, 0, 0, 0.05);
                    }
                    `;
            case 'warning':
                return `
                    background-color: ${theme.colors.bgPrimary};
                    border: 1px solid;
                    color: ${theme.colors.generalError};

                    &:after {
                        background-color: rgba(0, 0, 0, 0.05);
                    }
                    `;
            default:
                return '';
        }
    }}

    &[disabled] {
        pointer-events: none;
        background-color: ${({ theme }): string => theme.colors.controlDisabledBg};
        color: ${({ theme }): string => theme.colors.controlDisabledBody};
        border: 0;
    }

    &:hover {
        &:after {
            width: 100%;
        }
    }
`;

const ButtonContent = styled.span`
    font-size: 0.9375rem;
    font-weight: bold;
    position: relative;
    display: block;
    // z-index: 2; /* for hover effect to work "behind" it */
`;

export default BlockButton;
